import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = loading, false = not auth, object = user
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch (e) {
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = async (email, password, recaptcha_token) => {
        // Backend sets the access/refresh tokens as HttpOnly cookies — no
        // client-side storage of the JWT (XSS-safe).
        const { data } = await api.post("/auth/login", { email, password, recaptcha_token });
        await refresh();
        return data.user;
    };

    const registerCompany = async (payload) => {
        // payload may already include recaptcha_token from the caller.
        const { data } = await api.post("/auth/register-company", payload);
        await refresh();
        return data.user;
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch (e) {
            // Network/CORS errors during logout are non-fatal: the cookie expires
            // server-side and we clear local state below regardless.
            // eslint-disable-next-line no-console
            console.warn("[auth] logout request failed (continuing local sign-out):", e?.message || e);
        }
        // Clean up any legacy localStorage token from older builds.
        try {
            localStorage.removeItem("ihtp_token");
        } catch (e) {
            // SSR / private-mode browsers may block localStorage. Swallow + log.
            // eslint-disable-next-line no-console
            console.warn("[auth] could not clear legacy token from storage:", e?.message || e);
        }
        setUser(false);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, registerCompany, logout, refresh, formatApiError }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
